import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Product, Review } from '../types/types';
import { ROUTES } from '../data/constants';
import dataService from '../data/dataService';
import '../styles/Reviews.css';

const Reviews: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [allReviews, setAllReviews] = useState<{ review: Review; productName: string; productId: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReviews = async () => {
      setLoading(true);
      const prods = await dataService.getProducts();
      setProducts(prods);

      const reviewEntries: { review: Review; productName: string; productId: number }[] = [];
      for (const prod of prods) {
        const revs = await dataService.getReviews(prod.id);
        revs.forEach(r => reviewEntries.push({ review: r, productName: prod.name, productId: prod.id }));
      }
      reviewEntries.sort((a, b) => new Date(b.review.createdAt).getTime() - new Date(a.review.createdAt).getTime());
      setAllReviews(reviewEntries);
      setLoading(false);
    };
    loadReviews();
  }, []);

  const avgRating = allReviews.length > 0
    ? allReviews.reduce((sum, r) => sum + r.review.rating, 0) / allReviews.length
    : 0;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="reviews-page">
      <div className="reviews-header">
        <h1>Customer Reviews</h1>
        <p className="reviews-summary">
          {allReviews.length} reviews across {products.length} products — Average rating: {' '}
          <span className="avg-rating">
            {'★'.repeat(Math.round(avgRating))}{'☆'.repeat(5 - Math.round(avgRating))} {avgRating.toFixed(1)}
          </span>
        </p>
      </div>

      {allReviews.length > 0 ? (
        <div className="reviews-grid">
          {allReviews.map(({ review, productName, productId }) => (
            <div key={review.id} className="review-card">
              <div className="review-card-header">
                <span className="review-rating">
                  {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                </span>
                <span className="review-date">
                  {new Date(review.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="review-comment">{review.comment}</p>
              <div className="review-card-footer">
                <span className="review-author">— {review.reviewer}</span>
                <Link to={`/product/${productId}`} className="review-product-link">
                  {productName}
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <span className="no-results-icon">📝</span>
          <h3>No reviews yet</h3>
          <p>Be the first to review a product!</p>
          <Link to={ROUTES.PRODUCTS} className="btn btn-primary">
            Browse Products
          </Link>
        </div>
      )}
    </div>
  );
};

export default Reviews;
